import { IAccessRequest } from 'interfaces/AccessRequest/IAccessRequest';
import { IAccessRequestUpdate } from 'interfaces/AccessRequest/IAccessRequestUpdate';
import { useCallback } from 'react';
import { pricelistRoute } from '../../helpers/apiRoutes';
import { useApiCalls } from '../UseApiCalls';

export const PricelistApis = () => {
  const { getQuery, postQuery, getQueryFileDownload } = useApiCalls();

  const getMasterPricelist = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-master-pricelist`);
  }, [getQuery]);

  const getPricelistFilters = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-pricelist-filters`);
  }, [getQuery]);

  const getAvailablePricelists = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-available-pricelists`);
  }, [getQuery]);

  const getAllPricelists = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-all-pricelists`);
  }, [getQuery]);

  const getClientPricelist = useCallback(
    (client) => {
      return getQuery(`${pricelistRoute}/get-client-pricelist?client=${encodeURIComponent(client)}`);
    },
    [getQuery]
  );

  const getClientPricelistDownload = useCallback(
    (client) => {
      return getQueryFileDownload(`${pricelistRoute}/download-client-pricelist?client=${encodeURIComponent(client)}`);
    },
    [getQueryFileDownload]
  );

  const getAnalysisExcelDownload = useCallback((analysis, matrix) => {
    return getQueryFileDownload(`${pricelistRoute}/download-analysis-details?analysis=${encodeURIComponent(analysis)}&matrix=${encodeURIComponent(matrix)}`);
  }, [getQueryFileDownload]);

  const getAnalysisInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-analysis-info?analysis=${encodeURIComponent(analysis)}&matrix=${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getAnalyteInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-analyte-info?analysis=${encodeURIComponent(analysis)}&matrix=${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getGroupAnalysisInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-group-analysis-info?analysis=${encodeURIComponent(analysis)}&matrix=${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getPrepAnalysisInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-prep-analysis-info?analysis=${encodeURIComponent(analysis)}&matrix=${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getSubAnalysisInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-sub-analysis-info?analysis=${encodeURIComponent(analysis)}&matrix=${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getSubAnalysisAnalyteInfo = useCallback(
    (analysis, matrix, analyte, isGroupAnalysis) => {
      return getQuery(
        `${pricelistRoute}/get-sub-analysis-analyte-info?analysis=${encodeURIComponent(analysis)}&matrix=${encodeURIComponent(matrix)}&analyte=${encodeURIComponent(
          analyte)}&isGroupAnalysis=${isGroupAnalysis}`
      );
    },
    [getQuery]
  );

  const getMediaInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-media-info?analysis=${encodeURIComponent(analysis)}&matrix=${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getTrackingAnalysisDetails = useCallback(
    (trackingAnalysis) => {
      return getQuery(`${pricelistRoute}/get-tracking-analysis-details?trackinAnalysis=${encodeURIComponent(trackingAnalysis)}`);
    },
    [getQuery]
  );

  const getOtherInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-other-info?analysis=${encodeURIComponent(analysis)}&matrix=${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getManagedClients = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-managed-clients`);
  }, [getQuery]);

  const getAccess = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-access`);
  }, [getQuery]);

  const getClientAccess = useCallback(
    (client) => {
      return getQuery(`${pricelistRoute}/get-client-access?client=${encodeURIComponent(client)}`);
    },
    [getQuery]
  );

  const postRequestAccess = useCallback(
    (accessId) => {
      const data = {
        Id: accessId,
      };
      return postQuery(`${pricelistRoute}/request-access`, data);
    },
    [postQuery]
  );

  const postSetPricelistAccess = useCallback((data: IAccessRequest) => {
    return fetch(`${pricelistRoute}/set-access`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }).then((response) => response.json());
  }, []);

  const getPricelistAccessDetails = useCallback((data: IAccessRequest) => {
    return fetch(`${pricelistRoute}/get-access-request-details?id=${encodeURIComponent(data.Id)}&token=${encodeURIComponent(data.Token)}&accessType=${encodeURIComponent(data.AccessType)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response) => response.json());
  }, []);

  const postUpdateAccess = useCallback(
    (data: IAccessRequestUpdate) => {
      return postQuery(`${pricelistRoute}/update-access`, data);
    },
    [postQuery]
  );

  return {
    getClientPricelistDownload,
    getMasterPricelist,
    getPricelistFilters,
    getAvailablePricelists,
    getClientPricelist,
    getAnalysisInfo,
    getAnalyteInfo,
    getGroupAnalysisInfo,
    getPrepAnalysisInfo,
    getSubAnalysisInfo,
    getSubAnalysisAnalyteInfo,
    getMediaInfo,
    getTrackingAnalysisDetails,
    getOtherInfo,
    getAllPricelists,
    getAccess,
    getManagedClients,
    getClientAccess,
    postRequestAccess,
    postSetPricelistAccess,
    postUpdateAccess,
    getPricelistAccessDetails,
    getAnalysisExcelDownload
  };
};
